// @ts-nocheck
import * as d3 from "d3";
import { TrackRendererProps, RendererResult } from "./types";
import { TrackPoint } from "../hooks/useTrackRendering";
import { warn, debug } from "../../../../utils/console";
import { allSelectedEventIds, cutEvents, isCut, selectedEvents, selectedRanges } from "../../../../store/selectionStore";
import { resolveSelectionRangeStrokeColor } from "../../../../utils/colorScale";
import { applyManeuverPointToSelection } from "../utils/maneuverEventSelection";
import { defaultChannelsStore } from "../../../../store/defaultChannelsStore";
import { computeTrackImportance, simplifyTrackRun, trackSimplifyTolerance } from "../utils/trackGeometry";

export function renderSegmentedTracks(props: TrackRendererProps): RendererResult {
  try {
    debug(`[SegmentedTrackRenderer] ENTRY: renderSegmentedTracks called`);
    
    const { data, map, trackOverlay, config, samplingFrequency, onPointClick, tilesAvailable, getColor, getThickness, maneuversEnabled } = props;
    const cfg: any = config || {};
    
    debug(`[SegmentedTrackRenderer] Props extracted`, {
      dataLength: data?.length || 0,
      hasMap: !!map,
      hasTrackOverlay: !!trackOverlay,
      zoomLevel: cfg.zoomLevel,
      maptype: cfg.maptype
    });
  
    // Get dynamic channel names from store
    const { latName, lngName } = defaultChannelsStore;
    
    // Helper function to get Lat/Lng values with case-insensitive fallback
    const getLat = (d: any): number | undefined => {
      if (!d) return undefined;
      const latField = latName();
      // Try multiple field name variations for robustness
      const val = d[latField];
      if (val === undefined || val === null) return undefined;
      const numVal = Number(val);
      return isNaN(numVal) ? undefined : numVal;
    };
    
    const getLng = (d: any): number | undefined => {
      if (!d) return undefined;
      const lngField = lngName();
      // Try multiple field name variations for robustness
      const val = d[lngField];
      if (val === undefined || val === null) return undefined;
      const numVal = Number(val);
      return isNaN(numVal) ? undefined : numVal;
    };
    
    // Clear existing tracks and maneuvers
    trackOverlay.selectAll(".interactive-line").remove();
    trackOverlay.selectAll(".maneuver-group").remove();

    if (!data || data.length === 0) {
      return { success: true };
    }

    // Check if we're in cut mode and get cut ranges - ALWAYS read fresh values
    // Don't cache these - they need to be reactive to state changes
    const currentIsCut = isCut();
    const currentCutEvents = cutEvents();
    const hasCutRanges = currentIsCut && currentCutEvents && currentCutEvents.length > 0;
    
    // Helper to check which cut range a point belongs to (if any)
    // Returns -1 if point is outside all cut ranges, or the index of the range it belongs to
    const getCutRangeIndex = (point: TrackPoint): number => {
      if (!hasCutRanges) return -1;
      const pointTime = new Date(point.Datetime).getTime();
      
      for (let i = 0; i < currentCutEvents.length; i++) {
        const range = currentCutEvents[i];
        if (typeof range === 'number') continue; // Skip event IDs
        
        if (range.start_time && range.end_time) {
          const startTime = new Date(range.start_time).getTime();
          const endTime = new Date(range.end_time).getTime();
          if (pointTime >= startTime && pointTime <= endTime) {
            return i;
          }
        }
      }
      return -1; // Point doesn't belong to any cut range
    };

    // Collect drawable segment edges, then merge consecutive segments with identical stroke
    // so we render polylines (few DOM nodes) instead of one SVG <line> per edge (~12k+).
    type PreparedSeg = { p0: TrackPoint; p1: TrackPoint; color: string; strokeWidth: string | number };
    const prepared: PreparedSeg[] = [];
    let segmentsSkipped = 0;

    const findClosestVertex = (event: MouseEvent, candidates: TrackPoint[]): TrackPoint => {
      const [mouseX, mouseY] = d3.pointer(event, trackOverlay.node());
      let closestPoint = candidates[0];
      let minDistance = Infinity;
      for (const point of candidates) {
        const pointLat = getLat(point);
        const pointLng = getLng(point);
        if (pointLat === undefined || pointLng === undefined || isNaN(pointLat) || isNaN(pointLng)) continue;
        const mapPoint = map.project([pointLng, pointLat]);
        const distance = Math.hypot(mouseX - mapPoint.x, mouseY - mapPoint.y);
        if (distance < minDistance) {
          minDistance = distance;
          closestPoint = point;
        }
      }
      return closestPoint;
    };

    debug(`[SegmentedTrackRenderer] Building segments from ${data.length - 1} potential edges`);

    for (let i = 0; i < data.length - 1; i++) {
      const curr = data[i];
      const next = data[i + 1];

      const currLat = getLat(curr);
      const currLng = getLng(curr);
      const nextLat = getLat(next);
      const nextLng = getLng(next);

      if (currLng === undefined || currLat === undefined || nextLng === undefined || nextLat === undefined ||
          isNaN(currLng) || isNaN(currLat) || isNaN(nextLng) || isNaN(nextLat)) {
        segmentsSkipped++;
        continue;
      }

      const zoomLevel = cfg.zoomLevel ?? 16;
      const disableGapDetection = zoomLevel < 16;

      let isCutRangeBoundary = false;
      if (hasCutRanges && currentCutEvents && currentCutEvents.length > 0) {
        const currRangeIndex = getCutRangeIndex(curr);
        const nextRangeIndex = getCutRangeIndex(next);
        if (currRangeIndex !== nextRangeIndex) {
          isCutRangeBoundary = true;
        }
      }

      const expectedInterval = 1000 / samplingFrequency;
      const gapThreshold = (cfg && cfg.gapThreshold) ? cfg.gapThreshold : Math.min(expectedInterval * 3, 1000);
      const timeDiff = Math.abs(new Date(curr.Datetime).getTime() - new Date(next.Datetime).getTime());
      const legBoundaryBridgeMs = 2500;
      const prevLeg = curr.leg_number ?? curr.Leg_number ?? curr.LEG;
      const nextLeg = next.leg_number ?? next.Leg_number ?? next.LEG;
      const prevLegNum = prevLeg !== undefined && prevLeg !== null && !isNaN(Number(prevLeg)) ? Number(prevLeg) : undefined;
      const nextLegNum = nextLeg !== undefined && nextLeg !== null && !isNaN(Number(nextLeg)) ? Number(nextLeg) : undefined;
      const isConsecutiveLegBoundary =
        prevLegNum !== undefined &&
        nextLegNum !== undefined &&
        timeDiff > gapThreshold &&
        timeDiff <= legBoundaryBridgeMs &&
        Math.abs(nextLegNum - prevLegNum) === 1;
      const bridgeLegBoundary = isConsecutiveLegBoundary && !isCutRangeBoundary;

      if (!disableGapDetection) {
        if ((timeDiff > gapThreshold || isCutRangeBoundary) && !bridgeLegBoundary) {
          segmentsSkipped++;
          continue;
        }
      } else if (isCutRangeBoundary) {
        segmentsSkipped++;
        continue;
      }

      const strokeColor = getColor(curr, next, cfg);
      const strokeWidth = getThickness(curr, next, cfg);
      prepared.push({ p0: curr, p1: next, color: strokeColor, strokeWidth });
    }

    type MergedRun = { points: TrackPoint[]; color: string; strokeWidth: string | number };
    const runs: MergedRun[] = [];
    const styleKey = (c: string, w: string | number) => `${c}\0${String(w)}`;

    for (const seg of prepared) {
      const last = runs[runs.length - 1];
      if (
        last &&
        styleKey(last.color, last.strokeWidth) === styleKey(seg.color, seg.strokeWidth) &&
        last.points[last.points.length - 1] === seg.p0
      ) {
        last.points.push(seg.p1);
      } else {
        runs.push({ points: [seg.p0, seg.p1], color: seg.color, strokeWidth: seg.strokeWidth });
      }
    }

    // Drop colinear samples inside each color run. The stroke stays run.color; endpoints stay so
    // the next color still starts where this one ends. Hit-testing keeps the original vertices.
    const trackImportance = computeTrackImportance(data, getLat, getLng);
    const simplifyTolerance = trackSimplifyTolerance(cfg.zoomLevel ?? 16);

    const pathLine = d3
      .line<TrackPoint>()
      .x((d) => {
        const la = getLat(d);
        const ln = getLng(d);
        if (la === undefined || ln === undefined || isNaN(la) || isNaN(ln)) return 0;
        return map.project([ln, la]).x;
      })
      .y((d) => {
        const la = getLat(d);
        const ln = getLng(d);
        if (la === undefined || ln === undefined || isNaN(la) || isNaN(ln)) return 0;
        return map.project([ln, la]).y;
      });

    for (const run of runs) {
      if (run.points.length < 2) continue;
      const drawn = simplifyTrackRun(run.points, trackImportance, simplifyTolerance);
      const dPath = pathLine(drawn);
      if (!dPath) continue;

      const path = trackOverlay
        .append("path")
        .datum(run.points)
        .attr("class", "interactive-line segmented-track-path")
        .attr("d", dPath)
        .attr("stroke", run.color)
        .attr("stroke-width", run.strokeWidth)
        .attr("fill", "none")
        .style("opacity", 1)
        .on("click", (event: MouseEvent) => {
          event.stopPropagation();
          if (onPointClick) {
            onPointClick(findClosestVertex(event, run.points));
          }
        });

      if (props.onMouseOver && props.onMouseOut) {
        path
          .on("mouseover", (event: MouseEvent) => {
            props.onMouseOver(event, findClosestVertex(event, run.points));
          })
          .on("mouseout", (event: MouseEvent) => {
            props.onMouseOut(event);
          });
      }
    }

    debug(
      `[SegmentedTrackRenderer] Drawn ${runs.length} merged paths (${prepared.length} drawable edges), skipped ${segmentsSkipped} edges`
    );

    // Render selection overlays on top of base track
    // Check for selectedRanges (event selections) - only event selections get colored overlays
    const currentSelectedRanges = selectedRanges();
    const currentSelectedEvents = allSelectedEventIds(); // Palette index space — see resolveSelectionRangeStrokeColor
    const hasEventSelections = currentSelectedRanges && currentSelectedRanges.length > 0;
    
    if (hasEventSelections && currentSelectedRanges.length > 0) {
      debug('SegmentedTrackRenderer: Rendering', currentSelectedRanges.length, 'selected ranges (events)');
      
      // Loop through each selected range and render it on top
      currentSelectedRanges.forEach((range, rangeIndex) => {
        const startTime = new Date(range.start_time).getTime();
        const endTime = new Date(range.end_time).getTime();
        
        debug('SegmentedTrackRenderer: Processing range', rangeIndex, 'from', range.start_time, 'to', range.end_time);
        
        // Filter data to only include points within this time range
        const rangeData = data.filter(point => {
          const timestamp = new Date(point.Datetime).getTime();
          return timestamp >= startTime && timestamp <= endTime;
        });
        
        debug('SegmentedTrackRenderer: Range', rangeIndex, 'has', rangeData.length, 'points');

        if (rangeData.length > 0) {
          // Draw segments for this range with thicker lines and different colors
          let overlaySegmentsDrawn = 0;
          // Constant per range — resolving it inside the per-edge loop repeated the work for every segment.
          const rangeColor = resolveSelectionRangeStrokeColor(
            range,
            rangeIndex,
            currentSelectedRanges.length,
            currentSelectedEvents
          );

          for (let i = 0; i < rangeData.length - 1; i++) {
            const curr = rangeData[i];
            const next = rangeData[i + 1];
            
            // Get coordinates using dynamic channel names
            const currLat = getLat(curr);
            const currLng = getLng(curr);
            const nextLat = getLat(next);
            const nextLng = getLng(next);
            
            // Safety check for missing or invalid coordinates
            if (currLng === undefined || currLat === undefined || nextLng === undefined || nextLat === undefined ||
                isNaN(currLng) || isNaN(currLat) || isNaN(nextLng) || isNaN(nextLat)) {
              continue;
            }
            
            const x1 = map.project([currLng, currLat]).x;
            const y1 = map.project([currLng, currLat]).y;
            const x2 = map.project([nextLng, nextLat]).x;
            const y2 = map.project([nextLng, nextLat]).y;
            
            // Draw overlay segment with thicker line and range color
            const overlayLine = trackOverlay.append("line")
              .attr("class", "interactive-line selection-overlay")
              .attr("x1", x1)
              .attr("y1", y1)
              .attr("x2", x2)
              .attr("y2", y2)
              .attr("stroke", rangeColor)
              .attr("stroke-width", 3) // Thicker line for selections
              .attr("stroke-linecap", "round")
              .style("pointer-events", "all")
              .style("cursor", "pointer")
              .style("z-index", "10") // Ensure selections appear on top
              .on("click", (event) => {
                event.stopPropagation();
                if (onPointClick) {
                  // Find the closest point to the click
                  const [mouseX, mouseY] = d3.pointer(event, trackOverlay.node());
                  let closestPoint = curr;
                  let minDistance = Infinity;

                  [curr, next].forEach(point => {
                    const pointLat = getLat(point);
                    const pointLng = getLng(point);
                    if (pointLat === undefined || pointLng === undefined || isNaN(pointLat) || isNaN(pointLng)) return;
                    const mapPoint = map.project([pointLng, pointLat]);
                    const distance = Math.sqrt(
                      Math.pow(mouseX - mapPoint.x, 2) + Math.pow(mouseY - mapPoint.y, 2)
                    );
                    if (distance < minDistance) {
                      minDistance = distance;
                      closestPoint = point;
                    }
                  });

                  onPointClick(closestPoint);
                }
              });

            // Add tooltip handlers for overlay segments
            if (props.onMouseOver && props.onMouseOut) {
              overlayLine.on("mouseover", (event) => {
                props.onMouseOver(event, curr);
              })
              .on("mouseout", (event) => {
                props.onMouseOut(event);
              });
            }
            
            overlaySegmentsDrawn++;
          }
          
          debug('SegmentedTrackRenderer: Range', rangeIndex, 'drew', overlaySegmentsDrawn, 'overlay segments');
        }
      });
    }

    // Add maneuver circles and labels (only if enabled)
    if (maneuversEnabled !== false) {
      const maneuverData = data.filter((d) => d.Maneuver_type && d.Maneuver_type.length === 1 && /[A-Za-z]/.test(d.Maneuver_type));
      
      const isTilesAvailable = tilesAvailable !== false; // default true

      // Helper function to handle maneuver click - adds event_id to selectedEvents
      const handleManeuverClick = async (event: MouseEvent, d: TrackPoint) => {
        event.stopPropagation();
        event.preventDefault();
        debug("SegmentedTrackRenderer: Maneuver clicked", {
          Maneuver_type: d.Maneuver_type,
          Datetime: d.Datetime
        });
        try {
          await applyManeuverPointToSelection(d as unknown as Record<string, unknown>, "SegmentedTrackRenderer");
        } catch (error) {
          warn("SegmentedTrackRenderer: Error in maneuver click handler", error);
        }
      };

      // Create a group for each maneuver to handle hover on both circle and label
      // Render maneuvers AFTER track lines so they're on top and clickable
      const maneuverGroups = trackOverlay.selectAll(".maneuver-group")
        .data(maneuverData)
        .enter()
        .append("g")
        .attr("class", "maneuver-group")
        .style("cursor", "pointer")
        .style("pointer-events", "all")
        .style("z-index", "1000")
        .on("click", (event, d) => {
          warn('SegmentedTrackRenderer: Click event received on group');
          handleManeuverClick(event, d);
        });

      // Pad the hit area. The visible dot is 3px; the pointer only appeared when the cursor was on it.
      maneuverGroups.append("circle")
        .attr("class", "map-maneuver-hit-target")
        .attr("cx", (d: TrackPoint) => {
          const dLat = getLat(d);
          const dLng = getLng(d);
          if (dLat === undefined || dLng === undefined || isNaN(dLat) || isNaN(dLng)) return 0;
          return map.project([dLng, dLat]).x;
        })
        .attr("cy", (d: TrackPoint) => {
          const dLat = getLat(d);
          const dLng = getLng(d);
          if (dLat === undefined || dLng === undefined || isNaN(dLat) || isNaN(dLng)) return 0;
          return map.project([dLng, dLat]).y;
        })
        .attr("r", 22);

      // Add circles to groups
      maneuverGroups.append("circle")
        .attr("class", "maneuver-circle")
        .attr("cx", (d: TrackPoint) => {
          const dLat = getLat(d);
          const dLng = getLng(d);
          // Validate coordinates before calling map.project() to avoid NaN errors
          if (dLat === undefined || dLng === undefined || isNaN(dLat) || isNaN(dLng)) {
            warn('SegmentedTrackRenderer: Skipping maneuver - missing coordinates:', { Lng: dLng, Lat: dLat });
            return 0;
          }
          return map.project([dLng, dLat]).x;
        })
        .attr("cy", (d: TrackPoint) => {
          const dLat = getLat(d);
          const dLng = getLng(d);
          // Validate coordinates before calling map.project() to avoid NaN errors
          if (dLat === undefined || dLng === undefined || isNaN(dLat) || isNaN(dLng)) {
            return 0;
          }
          return map.project([dLng, dLat]).y;
        })
        .attr("r", 3)
        .style("fill", isTilesAvailable ? "white" : "black")
        .style("opacity", 0.8)
        .style("pointer-events", "all")
        .style("transition", "r 0.2s ease")
        .on("click", (event, d) => {
          warn('SegmentedTrackRenderer: Click event received on circle');
          handleManeuverClick(event, d);
        });

      // Add labels to groups
      maneuverGroups.append("text")
        .attr("class", "maneuver-label non-selectable")
        .attr("x", (d: TrackPoint) => {
          const dLat = getLat(d);
          const dLng = getLng(d);
          // Validate coordinates before calling map.project() to avoid NaN errors
          if (dLat === undefined || dLng === undefined || isNaN(dLat) || isNaN(dLng)) return 0;
          return map.project([dLng, dLat]).x + 5;
        })
        .attr("y", (d: TrackPoint) => {
          const dLat = getLat(d);
          const dLng = getLng(d);
          // Validate coordinates before calling map.project() to avoid NaN errors
          if (dLat === undefined || dLng === undefined || isNaN(dLat) || isNaN(dLng)) return 0;
          return map.project([dLng, dLat]).y + 5;
        })
        .text((d) => d.Maneuver_type)
        .style("font-size", "14px")
        .style("font-weight", "bold")
        .attr("user-select", "none")
        .style("fill", isTilesAvailable ? "white" : "black")
        .style("opacity", 0.8)
        .style("pointer-events", "all")
        .style("transition", "font-size 0.2s ease")
        .on("click", (event, d) => {
          warn('SegmentedTrackRenderer: Click event received on label');
          handleManeuverClick(event, d);
        });

      // Add hover effects to groups
      maneuverGroups
        .on("mouseover", function(event, d) {
          // Make circle bigger
          d3.select(this).select(".maneuver-circle")
            .attr("r", 6)
            .style("opacity", 1);
          // Make label bigger
          d3.select(this).select(".maneuver-label")
            .style("font-size", "20px")
            .style("opacity", 1);
        })
        .on("mouseout", function(d) {
          // Make circle smaller
          d3.select(this).select(".maneuver-circle")
            .attr("r", 3)
            .style("opacity", 0.8);
          // Make label smaller
          d3.select(this).select(".maneuver-label")
            .style("font-size", "14px")
            .style("opacity", 0.8);
        });
    }

    debug(`[SegmentedTrackRenderer] SUCCESS: Returning success`);
    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : undefined;
    debug(`[SegmentedTrackRenderer] ERROR: ${errorMessage}`, { stack: errorStack });
    warn(`[SegmentedTrackRenderer] Render failed:`, error);
    return { success: false, error: errorMessage };
  }
}

